/**
 * NHL API client — isolated. Hits /nhl/* endpoints. A failed fetch returns an
 * HONEST EMPTY shape (never fabricated rows) flagged `_sample:true`, so each
 * surface can show "live data unavailable" rather than invented numbers.
 */
const BASE = import.meta.env.VITE_API_URL || 'https://linebrkr-deploy-production.up.railway.app'

// Reuse the same access code the MLB/NFL clients store so /nhl/* board reads pass
// the global gate when the owner/beta user is signed in. No code (dev) → omitted.
const _hdrs = () => {
  try {
    const c = localStorage.getItem('brkr_access')
    return c ? { 'x-access-code': c } : {}
  } catch (_) { return {} }
}

async function tryGet(path, empty) {
  try {
    const r = await fetch(`${BASE}${path}`, { headers: _hdrs() })
    if (r.ok) return await r.json()
    return { ...(empty || {}), _sample: true, _status: r.status }
  } catch (e) {
    return { ...(empty || {}), _sample: true }
  }
}

const _d = date => (date ? `&date=${encodeURIComponent(date)}` : '')

export { NHL_MARKETS, MARKET_LABEL } from './ui'

export const getNHLStatus = () =>
  tryGet('/nhl/status', { season: '', projections_today: 0, markets: [], recent_jobs: [] })

export const getNHLSlate = (date) =>
  tryGet(`/nhl/slate?_=1${_d(date)}`, { games: [] })

export const getNHLLeaderboard = (market, date, limit = 250) =>
  tryGet(`/nhl/projections/leaderboard?limit=${limit}${market ? `&market=${market}` : ''}${_d(date)}`,
         { plays: [], count: 0 })

export const getNHLSignals = (date) =>
  tryGet(`/nhl/signals?_=1${_d(date)}`,
         { shootouts: [], weak_pk: [], back_to_backs: [], goalie_spots: [] })

export const getNHLPlayerLogs = (playerId, limit = 15) =>
  tryGet(`/nhl/player/${playerId}?limit=${limit}`, { kind: 'skater', logs: [] })

export const getNHLBookOdds = (date, market) =>
  tryGet(`/nhl/book-odds?_=1${market ? `&market=${market}` : ''}${_d(date)}`, { odds: [] })

export const getNHLBookOddsLadder = (playerId, market, date) =>
  tryGet(`/nhl/book-odds-ladder?player_id=${playerId}&market=${market}${_d(date)}`, { rungs: [], best_value: null })

export const getNHLResults = (date, scope = 'recommended') =>
  tryGet(`/nhl/results?scope=${scope}${_d(date)}`, { card: [], record: {}, realized: {} })

export const getNHLScorecard = () =>
  tryGet('/nhl/scorecard', { recommended: {}, realized: {}, by_prop: {}, n_recommended: 0 })

export const getNHLTierRecord = () =>
  tryGet('/nhl/tier-record', { tiers: {} })

export const getNHLCalibration = () =>
  tryGet('/nhl/calibration', { buckets: [] })

// Public (ungated) surfaces.
export const getNHLProof = () =>
  tryGet('/nhl/proof', { scorecard: {}, tiers: {} })

export const getNHLPreview = () =>
  tryGet('/nhl/preview', { top: null, recommended_count: 0, total_plays: 0 })

// Access probe: /nhl/slate is behind the global code gate. 401 = locked (needs a
// code); anything else (incl. ok) = open. NHL has no separate sport entitlement.
export async function checkNHLAccess() {
  try {
    const r = await fetch(`${BASE}/nhl/slate`, { headers: _hdrs() })
    if (r.status === 401) return 'locked'
    return 'open'
  } catch (_) { return 'open' }
}
